import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, CaretDown, CaretUp, Compass } from "@phosphor-icons/react";
import { FOUNDER, FOUNDER_BLOCKS, ROADMAP_BLOCKS } from "@/data/founder";
import FounderBody from "@/components/FounderBody";
import ShareButton from "@/components/ShareButton";
import ListenTranslateBar from "@/components/ListenTranslateBar";

/**
 * /about/founder — permanent, shareable version of the "Why I Created
 * NaturePulse" letter. Linked from the site-wide <FounderNote /> callout
 * and from the About and Legal hubs.
 */
export default function FounderStory() {
  const [openRoadmap, setOpenRoadmap] = useState(false);
  return (
    <div className="min-h-screen pt-28 pb-24 px-4 md:px-8 text-[color:var(--np-text)]">
      <div className="max-w-[860px] mx-auto">
        <Link
          to="/about"
          data-testid="founder-back-link"
          className="inline-flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted)] hover:text-[color:var(--np-brand)]"
        >
          <ArrowLeft size={12} weight="bold" /> About NaturePulse
        </Link>

        <div className="mt-8 flex flex-col md:flex-row md:items-end gap-6 md:gap-10">
          <img
            src={FOUNDER.photoUrl}
            alt={FOUNDER.name}
            data-testid="founder-hero-photo"
            className="w-32 h-32 md:w-44 md:h-44 rounded-2xl object-cover shrink-0 border border-black/10 shadow-sm"
            loading="lazy"
          />
          <div className="min-w-0">
            <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand)]">
              / {FOUNDER.eyebrow}
            </div>
            <h1 className="mt-3 font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              {FOUNDER.title}
            </h1>
            <div className="mt-4 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted)]">
              {FOUNDER.name} · {FOUNDER.role}
            </div>
            <div className="mt-5">
              <ListenTranslateBar text={`${FOUNDER.title}. A note from ${FOUNDER.name}, ${FOUNDER.role} — why NaturePulse exists and where it is going.`} testidPrefix="founder-tools" />
            </div>
          </div>
        </div>

        <div className="mt-14">
          <FounderBody blocks={FOUNDER_BLOCKS} />
        </div>

        {/* Postscript — "What I would improve next" (roadmap) */}
        <div className="mt-14 rounded-3xl border border-[color:var(--np-brand)]/25 bg-[color:var(--np-brand)]/5 p-6 md:p-10">
          <div className="flex items-start gap-3">
            <span className="mt-1 inline-grid place-items-center w-8 h-8 rounded-full bg-[color:var(--np-brand)]/15 text-[color:var(--np-brand)]">
              <Compass size={16} weight="bold" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand)]">
                / P.S. — Founder's Roadmap
              </div>
              <h2 className="mt-2 font-serif-display text-2xl md:text-4xl leading-tight tracking-tight text-[color:var(--np-text)]">
                What I would improve next
              </h2>
              <p className="mt-4 max-w-[62ch] text-[15px] md:text-[16px] leading-relaxed text-[color:var(--np-text-2)]">
                A short list of ideas that would take NaturePulse from a publishing site into a true observation
                database — mapped, verifiable, and open to citizen scientists everywhere.
              </p>
              <button
                type="button"
                onClick={() => setOpenRoadmap((v) => !v)}
                data-testid="roadmap-toggle"
                aria-expanded={openRoadmap}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[color:var(--np-text)] text-[color:var(--np-bg)] hover:bg-[color:var(--np-brand)] transition-colors font-mono-tech text-[11px] tracking-[0.28em] uppercase px-5 py-2.5"
              >
                {openRoadmap ? (<>Collapse roadmap <CaretUp size={12} weight="bold" /></>) : (<>Read the full roadmap <CaretDown size={12} weight="bold" /></>)}
              </button>
              <div className="mt-3">
                <Link
                  to="/about/roadmap"
                  data-testid="roadmap-permalink"
                  className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand)] hover:underline"
                >
                  Open as a permalink page →
                </Link>
              </div>

              <AnimatePresence initial={false}>
                {openRoadmap && (
                  <motion.div
                    key="roadmap"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="pt-10">
                      <FounderBody blocks={ROADMAP_BLOCKS} />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* share */}
        <div className="mt-16 flex flex-wrap items-center gap-4 border-t border-black/10 pt-8">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted)]">
            Share this letter
          </div>
          <ShareButton
            title="Why I Created NaturePulse — from founder Tom Vellaringattu"
            text="A letter from Tom (Thomas) Vellaringattu, founder of NaturePulse, on documenting the pulse of our living planet."
            hashtags={["NaturePulse", "CitizenScience", "Nature"]}
            variant="pill"
            size="sm"
            testid="founder-share"
            shortKind="article"
            shortId="founder-letter"
          />
        </div>
      </div>
    </div>
  );
}
